import { useDatoClient, useUserIdentity } from '../stores-v2/StoreContext';

import type { ShowcaseAccess } from '@src/types.ts/story';

const getRedirectToSignInLink = (orgSlug?: string) => {
  if (!orgSlug) {
    return `${process.env.REACT_APP_API_URL}`;
  }

  return `${process.env.REACT_APP_API_URL}/showcase/${orgSlug}`;
};

const hasAccessToStory = (
  albums: ShowcaseAccess[] | undefined,
  memberships: Array<{ organization: { id: string; slug: string | null } }>,
  clerkUserPrimaryEmail: string,
) => {
  return !!albums?.find(album =>
    memberships?.find(
      memb =>
        (album.organization && memb.organization.id === album.organization) ||
        (album.slug && memb.organization.slug === album.slug) ||
        album.adminList?.find(admin => admin.email === clerkUserPrimaryEmail) ||
        album.allowList?.find(editor => editor.email === clerkUserPrimaryEmail),
    ),
  );
};

export function useAccessControl() {
  const userIdentityStore = useUserIdentity();
  const datoClientStore = useDatoClient();

  const checkUserAccess = async (
    storyId: string | null,
    showcase: string | undefined,
    isHomePage: boolean = false,
  ) => {
    const isDatoUser = userIdentityStore.isInternalUser();
    const clerkUser = userIdentityStore.clerkUser;
    const memberships = userIdentityStore.memberships;

    if (isDatoUser) {
      // console.log('Is Dato User - access granted');
      return { hasAccess: true };
    }

    if (!clerkUser) {
      console.log('User is not authenticated - redirecting to studio sign-in');
      // Send unauthenticated users to the studio-local sign-in page (not the
      // backend app) so we don't bounce between studio and app. Authenticated
      // users lacking access still go to the app showcase via the helper below.
      return { hasAccess: false, redirectTo: '/sign-in' };
    }

    if (
      clerkUser.emailAddresses.find(e =>
        e.emailAddress.endsWith('@tryarbor.com'),
      )
    ) {
      // console.log('access granted');
      return { hasAccess: true };
    }

    let hasOrgAccess;
    if (showcase) {
      hasOrgAccess = !!memberships.find(
        memb => memb.organization.slug === showcase,
      );
      if (!hasOrgAccess) {
        console.log('No org membership - access denied');
        return {
          hasAccess: false,
          redirectTo: getRedirectToSignInLink(showcase),
        };
      }
    }

    if (storyId) {
      const albums =
        await datoClientStore.albumRepository?.findAllWithStoryAccess(storyId);

      const hasStoryAccess = hasAccessToStory(
        albums,
        memberships,
        clerkUser.primaryEmailAddress?.emailAddress || '',
      );

      if (!hasStoryAccess) {
        console.log('No membership in story album - access denied');
        return {
          hasAccess: false,
          redirectTo: getRedirectToSignInLink(albums?.[0]?.slug || ''),
        };
      } else {
        // console.log('story access granted');
        return { hasAccess: true };
      }
    } else if (hasOrgAccess) {
      // console.log('showcase access granted');
      return { hasAccess: true };
    }

    if (isHomePage) {
      return { hasAccess: true };
    }

    console.log('access denied');
    return { hasAccess: false, redirectTo: getRedirectToSignInLink() };
  };

  return {
    checkUserAccess,
  };
}
